// Server-confirmed moves (S4).
//
// A board→board move is no longer applied optimistically to authoritative
// state on the acting client. On drop we emit a move INTENT and track it here
// as pending (no visual: the card itself already rests at the drop point).
// The real card commits to the new cell only when the server diff arrives
// (S2's cell fold relocates the mesh). If no confirming diff lands within
// PENDING_TTL_MS the move was rejected and every card is re-folded to its
// server truth (position + tap), so an illegal move REVERTS to the server
// cell instead of sticking, and it never bounces to hand.
//
// The TTL sits below the render-parity confirm window (2500ms. RenderParity
// CONFIRM_MS) so a rejected optimistic displacement heals itself before the
// checker would flag a play-position divergence.
//
// Host contract: meshes, _authMirror, _pendingMoves (owned here),
// _pendingMoveSweepTimer (owned here).
import { applyLivePositionsFromMirror } from './authZones';
import { applyCardStateFromMirror } from './authCardState';

const PENDING_TTL_MS = 1500;

// Register a pending move and start the sweep timer. Confirmed by
// resolvePendingMovesFromMirror (diff lands), or reverted by
// sweepPendingMoves (TTL elapses with no confirm).
export function beginPendingMove(host, card, toCell) {
  if (!card?.id || !toCell || typeof toCell.col !== 'number' || typeof toCell.row !== 'number') return;
  const pend = host._pendingMoves || (host._pendingMoves = new Map());
  pend.set(card.id, {
    toCol: toCell.col,
    toRow: toCell.row,
    deadline: Date.now() + PENDING_TTL_MS,
  });
  scheduleSweep(host);
}

// Fold hook: a pending move whose card now sits on the target cell in the mirror
// is CONFIRMED. The server committed it and the cell fold moved the real mesh.
export function resolvePendingMovesFromMirror(host, mirrorState) {
  const pend = host._pendingMoves;
  const cards = mirrorState?.cards;
  if (!pend?.size || !cards) return;
  for (const [id, rec] of [...pend]) {
    const cell = cards[id]?.cell;
    if (cell && cell.col === rec.toCol && cell.row === rec.toRow) {
      pend.delete(id);
    }
  }
}

// True while `cardId` has a move intent in flight that the mirror has not yet
// confirmed (its cell is not the requested target). The mirror folds treat
// such a card as locally owned: between the drop and the confirming diff the
// mirror still holds the PRE-move cell/pos/tap, so folding it over the
// optimistic apply made every unrelated diff untap-and-retap the mover (double
// tap sound) and glide its mesh back to the stale slot before the confirm
// re-placed it. The confirming diff flips this to false (cell matches) before
// resolvePendingMovesFromMirror deletes the entry, so that same diff folds
// normally; a rejected move heals through the TTL sweep's re-fold.
export function isMoveInFlight(host, cardId, mirrorState) {
  const rec = host._pendingMoves?.get(cardId);
  if (!rec) return false;
  const cell = mirrorState?.cards?.[cardId]?.cell;
  return !(cell && cell.col === rec.toCol && cell.row === rec.toRow);
}

// ── Pending position nudges ────────────────────────────────────────────────
// A same-cell reposition (and any other direct SET_POSITION commit) has no
// move intent and no ghost: the card already rests where the player dropped
// it and the command echo just persists those coords. Until that echo folds,
// the mirror still holds the pre-drop pos, so the live position fold would
// glide the card back to its stale slot on any diff arriving in the window
// (the release → bounce-back → glide-forward reposition bug). Registering the
// emitted coords lets the fold skip the card until the mirror catches up
// (pos matches) or the TTL lapses (command lost / coords not persisted).
const NUDGE_TTL_MS = 1500;

export function beginPendingNudge(host, cardId, x, z) {
  if (!cardId || typeof x !== 'number' || typeof z !== 'number') return;
  const pend = host._pendingNudges || (host._pendingNudges = new Map());
  pend.set(cardId, { x, z, deadline: Date.now() + NUDGE_TTL_MS });
}

export function isNudgeInFlight(host, cardId, mirrorState) {
  const rec = host._pendingNudges?.get(cardId);
  if (!rec) return false;
  if (Date.now() > rec.deadline) {
    host._pendingNudges.delete(cardId);
    return false;
  }
  const pos = mirrorState?.cards?.[cardId]?.pos;
  if (pos && Math.abs(pos.x - rec.x) < 1e-3 && Math.abs(pos.z - rec.z) < 1e-3) {
    host._pendingNudges.delete(cardId);
    return false;
  }
  return true;
}

// Timer body: any pending move past its deadline was rejected/lost. Re-fold
// every card to server truth so a stuck optimistic displacement
// (position + tap) reverts to the server cell.
export function sweepPendingMoves(host) {
  const pend = host._pendingMoves;
  if (!pend?.size) return;
  const now = Date.now();
  let reverted = false;
  for (const [id, rec] of [...pend]) {
    if (now < rec.deadline) continue;
    // Mid-drag: the re-fold below skips dragged meshes, so deleting the entry
    // now would cancel the revert forever (same race as the cast sweep).
    // Re-arm and revert right after the drag ends.
    const mesh = host.meshes?.get(id);
    if (mesh && host.dragging?.members?.some?.((dm) => dm.mesh === mesh)) {
      rec.deadline = now + DRAG_RETRY_MS;
      continue;
    }
    pend.delete(id);
    reverted = true;
  }
  if (reverted) {
    const m = host._authMirror?.getState?.();
    if (m) {
      applyLivePositionsFromMirror(host, m);
      applyCardStateFromMirror(host, m);
    }
  }
  if (pend.size) scheduleSweep(host);
}

export function clearAllPendingMoves(host) {
  host._pendingMoves?.clear();
  host._pendingNudges?.clear();
  if (host._pendingMoveSweepTimer) {
    clearTimeout(host._pendingMoveSweepTimer);
    host._pendingMoveSweepTimer = null;
  }
}

// ── Server-confirmed CASTS (S5) ────────────────────────────────────────────
// A realm play (minion / aura / artifact / site) is optimistically rendered as
// a board mesh on the acting client while the server validates the cast intent
// (the mirror keeps the card in zone 'hand' until the cast diff folds).
// `beginPendingCast` records that in-flight cast; `resolvePendingCastsFromMirror`
// drops the marker the instant the mirror confirms the play (zone 'play'); and
// `sweepPendingCasts` is the timer that REVERTS a cast the server rejected. An
// illegal cast produces zero patches, so no confirming diff ever lands, the
// optimistic mesh is removed and the card returned to hand.
//
// This replaces the old `_pendingCastZoneIds` map whose reject-bounce lived
// INSIDE applyBoardExitsFromMirror and therefore only fired when some unrelated
// diff happened to arrive. A rejected cast in a quiet game lingered as a
// phantom mesh until the next fold. The timer heals it unconditionally. The
// board-exits fold keeps the `_mirrorZoneSeen` transition guard (the only signal
// that separates an opponent's in-flight cast from its genuine return-to-hand,
// since both project as a skeleton in zone 'hand').
const CAST_TTL_MS = 4000;
// Re-arm delay when a sweep finds the phantom mid-drag: long enough to outlive
// the frame the drop lands on, short enough that the revert still feels
// immediate once the pointer lets go.
const DRAG_RETRY_MS = 400;

export function beginPendingCast(host, card) {
  if (!card?.id) return;
  const pend = host._pendingCasts || (host._pendingCasts = new Map());
  pend.set(card.id, Date.now() + CAST_TTL_MS);
  scheduleCastSweep(host);
}

export function resolvePendingCastsFromMirror(host, mirrorState) {
  const pend = host._pendingCasts;
  const cards = mirrorState?.cards;
  if (!pend?.size || !cards) return;
  for (const id of [...pend.keys()]) {
    if (cards[id]?.zone === 'play') pend.delete(id);
  }
}

// Timer body: a pending cast past its deadline that never reached zone 'play'
// was rejected by the server. Confirmed casts are recognised two ways. The
// live mirror shows it in play, or `_mirrorZoneSeen` observed it there (a cast
// that played then already left, e.g. died, keeps its 'play' history). And are
// skipped. Everything else is a genuine reject: drop the optimistic mesh and
// return the card to its owner's hand.
//
// Guard ORDER matters: the entry is deleted only once the revert (or a confirm)
// actually happened. The old shape deleted first and then bailed on the guards,
// so a player re-dragging the phantom exactly when the sweep fired cancelled
// the revert FOREVER. The phantom stuck until a manual recovery (the
// "rejected card lingers 20s+" bug). A mid-drag entry now just re-arms and the
// revert lands right after the drag ends.
export function sweepPendingCasts(host) {
  const pend = host._pendingCasts;
  if (!pend?.size) return;
  const now = Date.now();
  const cards = host._authMirror?.getState?.()?.cards;
  for (const [id, deadline] of [...pend]) {
    if (now < deadline) continue;
    if (cards?.[id]?.zone === 'play'                        // confirmed (live mirror)
      || host._mirrorZoneSeen?.get(id) === 'play') {        // confirmed (observed in play)
      pend.delete(id);
      continue;
    }
    const mesh = host.meshes?.get(id);
    if (!mesh) { pend.delete(id); continue; }               // nothing left to revert
    if (host.dragging?.members?.some?.((dm) => dm.mesh === mesh)) {
      pend.set(id, now + DRAG_RETRY_MS);                    // retry after the drag ends
      continue;
    }
    const card = mesh.userData?.cardInstance;
    if (!card) { pend.delete(id); continue; }
    pend.delete(id);
    // broadcast=true: the cosmetic card:place relay staged a phantom mesh on
    // the OPPONENT's table too (mirror zone stayed 'hand', so their board-exit
    // fold will never clean it). The revert must relay card:remove exactly
    // like a manual return-to-hand does, or the opponent keeps the phantom.
    host.removeCardFromTable(card, true);
    if (host.isCardOnLocalSide?.(card)) host.addToHand?.(card);
  }
  if (pend.size) scheduleCastSweep(host);
}

// Server told us the cast was rejected (match:intentRejected). Revert the
// optimistic play NOW instead of waiting out the TTL. Reuses the sweep body
// (same confirm guards + the mid-drag re-arm) by expiring the entry in place.
export function revertPendingCastNow(host, cardId) {
  const pend = host._pendingCasts;
  if (!cardId || !pend?.has(cardId)) return;
  pend.set(cardId, 0);
  sweepPendingCasts(host);
}

// Movement twin: an out-of-reach / region-blocked move was rejected. Snap the
// card back to its server cell immediately.
export function revertPendingMoveNow(host, cardId) {
  const pend = host._pendingMoves;
  if (!cardId || !pend?.has(cardId)) return;
  pend.get(cardId).deadline = 0;
  sweepPendingMoves(host);
}

export function clearAllPendingCasts(host) {
  host._pendingCasts?.clear();
  if (host._pendingCastSweepTimer) {
    clearTimeout(host._pendingCastSweepTimer);
    host._pendingCastSweepTimer = null;
  }
}

// Both schedulers fire at the NEAREST pending deadline (+100ms slack), not a
// fixed TTL. A drag re-armed entry (deadline now+400) resweeps right after
// the drag, and a second in-flight cast doesn't wait out the first one's timer.
function scheduleCastSweep(host) {
  if (host._pendingCastSweepTimer) return;
  const pend = host._pendingCasts;
  const soonest = pend?.size ? Math.min(...pend.values()) : Date.now() + CAST_TTL_MS;
  host._pendingCastSweepTimer = setTimeout(() => {
    host._pendingCastSweepTimer = null;
    if (!host._unmounted) sweepPendingCasts(host);
  }, Math.max(100, soonest - Date.now() + 100));
}

function scheduleSweep(host) {
  if (host._pendingMoveSweepTimer) return;
  const pend = host._pendingMoves;
  const soonest = pend?.size
    ? Math.min(...[...pend.values()].map((r) => r.deadline))
    : Date.now() + PENDING_TTL_MS;
  host._pendingMoveSweepTimer = setTimeout(() => {
    host._pendingMoveSweepTimer = null;
    if (!host._unmounted) sweepPendingMoves(host);
  }, Math.max(100, soonest - Date.now() + 100));
}

